import { Fragment, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Settings as SettingsIcon, Save, UserPlus, ShieldCheck, Globe2, Check, Ban, Pencil, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { PageHeader } from "@/components/control/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  Section, Loadable, Empty, Status, Tabbar, TabPanel, useTabs, ActionButton, ActionDialog, RunButton, useLoad, fmt, can, label, errText, COUNTRY_OPTIONS, TZ_OPTIONS,
} from "@/components/control/kit";
import { inviteFields, showInvite } from "@/pages/OrganizationDetail";
import { AdminConfigStatus } from "@/components/control/AdminConfigStatus";
import { RecoveryRequestsCard } from "@/components/control/RecoveryRequestsCard";
import { HandoffDocs } from "@/components/control/HandoffDocs";

const ROLE_OPTIONS = ["admin", "reviewer", "moderator", "support", "editor", "org_staff"].map((r) => ({ value: r, label: label(r) }));

// C120: organisation-bound accounts only read the organisations assigned here (server-enforced).
function OrgScopeButton({ m, onDone }) {
  const [open, setOpen] = useState(false);
  const st = useLoad(open ? "/organizations" : null);
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`change-orgs-${m.id}`}>Organisationen</Button>
      <ActionDialog open={open && !!st.data} onOpenChange={setOpen} title="Organisationen zuweisen" testId={`change-orgs-${m.id}-dialog`} submitLabel="Speichern" onDone={onDone}
        description="Das Konto sieht nur diese Organisationen und deren Aktivitäten. Alle anderen Organisationen bleiben gesperrt."
        fields={[{ name: "organization_ids", label: "Freigegebene Organisationen", type: "checks", default: m.organization_ids || [],
          options: (st.data?.organizations || []).map((o) => ({ value: o.id, label: o.name })) }]}
        onSubmit={async (v) => { await api.post(`/team/${m.id}/organizations`, v); return "Organisationen gespeichert."; }} />
    </>
  );
}

function CountriesEditor({ initial, canManage }) {
  const [rows, setRows] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (code, patch) => setRows((rs) => rs.map((r) => (r.code === code ? { ...r, ...patch } : r)));
  const save = async () => {
    setBusy(true);
    try { await api.put("/settings/countries", { countries: rows }); toast.success("Ländereinstellungen gespeichert."); }
    catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Table data-testid="countries-table">
        <TableHeader><TableRow><TableHead>Land</TableHead><TableHead>Zeitzone</TableHead><TableHead>Datumsformat</TableHead><TableHead>Aktiv</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.code}>
              <TableCell>{r.name}</TableCell>
              <TableCell>
                <select aria-label={`Zeitzone ${r.name}`} value={r.timezone} disabled={!canManage} onChange={(e) => set(r.code, { timezone: e.target.value })}
                  data-testid={`country-tz-${r.code}`} className="h-9 rounded-lg border border-input px-2 text-sm">
                  {TZ_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </TableCell>
              <TableCell>{r.date_format}</TableCell>
              <TableCell><input type="checkbox" aria-label={`${r.name} aktiv`} checked={r.active} disabled={!canManage}
                onChange={(e) => set(r.code, { active: e.target.checked })} data-testid={`country-active-${r.code}`} className="h-4 w-4 accent-[#D50B58]" /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {canManage && <Button onClick={save} disabled={busy} data-testid="save-countries" className="mt-4 gap-1.5 bg-brand-magenta hover:bg-brand-magenta-dark"><Save className="h-4 w-4" /> Speichern</Button>}
    </>
  );
}

function Regional() {
  const { user } = useAuth();
  const st = useLoad("/settings");
  return (
    <Section title={<span className="flex items-center gap-2"><Globe2 className="h-5 w-5 text-brand-magenta" /> Sprache und Länder</span>} testId="settings-regional">
      <Loadable state={st}>
        {(d) => (
          <>
            <p className="mb-4 text-sm">Sprache von Control OS: <strong>{d.language_label}</strong> (alle Texte, Fehlermeldungen und Hilfen sind deutsch).</p>
            <CountriesEditor initial={d.countries} canManage={user?.role === "super_admin"} />
            {d.updated_at && <p className="mt-3 text-xs text-muted-foreground">Zuletzt geändert {fmt(d.updated_at)} durch {d.updated_by}</p>}
          </>
        )}
      </Loadable>
    </Section>
  );
}

function Invitations() {
  const st = useLoad("/team/invitations");
  return (
    <Section title="Einladungen" testId="team-invitations" actions={
      <ActionButton label="Teammitglied einladen" icon={UserPlus} variant="default" testId="invite-team-member" onDone={st.reload} submitLabel="Einladung erstellen"
        page="team-einladen" pageSubtitle="Die Einladung wird mit einer klar begrenzten internen Rolle geprüft."
        description="Der Einladungslink ist einmalig und befristet. Den zweiten Faktor richtet die Person bei der ersten Anmeldung ein."
        fields={[...inviteFields(ROLE_OPTIONS), { name: "countries", label: "Länder (leer = alle)", type: "checks", options: COUNTRY_OPTIONS }]}
        onSubmit={async (v) => { const { data } = await api.post("/team/invitations", { ...v, days: Number(v.days) }); return showInvite(data); }} />
    }>
      <Loadable state={st}>
        {(d) => (d.invitations.length === 0 ? <Empty text="Keine Einladungen." /> : (
          <ul className="divide-y divide-border">
            {d.invitations.map((i) => (
              <li key={i.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`invitation-${i.id}`}>
                <div><p className="text-sm font-medium">{i.first_name} · {i.email}</p><p className="text-xs text-muted-foreground">{label(i.role)} · gültig bis {fmt(i.expires_at)}</p></div>
                <div className="flex items-center gap-2"><Status value={i.status} />
                  {i.status === "ausstehend" && <RunButton label="Widerrufen" icon={Ban} testId={`revoke-invitation-${i.id}`} success="Einladung widerrufen." onDone={st.reload}
                    run={() => api.post(`/team/invitations/${i.id}/revoke`)} />}</div>
              </li>
            ))}
          </ul>
        ))}
      </Loadable>
    </Section>
  );
}

export function Team() {
  const { user } = useAuth();
  const st = useLoad("/team");
  const isSuper = user?.role === "super_admin";
  return (
    <div className="space-y-5">
      <Section title="Team" testId="settings-team">
        <Loadable state={st}>
          {(d) => (
            <div className="overflow-x-auto">
              <Table data-testid="team-table">
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Rolle</TableHead><TableHead>Länder</TableHead><TableHead>Zweiter Faktor</TableHead>
                  <TableHead>Status</TableHead>{isSuper && <TableHead className="text-right">Aktionen</TableHead>}</TableRow></TableHeader>
                <TableBody>
                  {d.team.map((m) => (
                    <TableRow key={m.id} data-testid={`team-row-${m.id}`}>
                      <TableCell><p className="font-medium">{m.name}</p><p className="text-xs text-muted-foreground">{m.email}{m.is_demo ? " · Demo" : ""}</p>
                        {m.config_identity && <p className="text-xs font-medium text-brand-magenta" data-testid={`team-identity-${m.id}`}>{m.config_identity.label}</p>}</TableCell>
                      <TableCell>{m.role_label}</TableCell>
                      <TableCell className="text-sm">{m.countries.length ? m.countries.join(", ") : "alle"}
                        {m.role === "org_staff" && <p className="text-xs text-muted-foreground" data-testid={`team-orgs-${m.id}`}>{(m.organization_ids || []).length} Organisation(en)</p>}</TableCell>
                      <TableCell><Status value={m.twofa_confirmed ? "aktiv" : "ausstehend"} /></TableCell>
                      <TableCell><Status value={m.status === "active" ? "aktiv" : m.status} /></TableCell>
                      {isSuper && (
                        <TableCell className="text-right">
                          {m.role !== "super_admin" && (
                            <div className="flex justify-end gap-2">
                              <ActionButton label="Rolle" icon={Pencil} testId={`change-role-${m.id}`} onDone={st.reload} submitLabel="Rolle ändern" danger
                                page={`rolle-${m.id}`} title="Teamrolle ändern" pageRows={[["Teammitglied", m.name]]} pageSubject={m.name}
                                description="Ein Rollenwechsel beendet alle Sitzungen der Person."
                                fields={[{ name: "role", label: "Neue Rolle", type: "select", options: ROLE_OPTIONS, required: true, default: m.role },
                                  { name: "reason", label: "Begründung", type: "textarea", required: true }]}
                                onSubmit={async (v) => { await api.post(`/team/${m.id}/role`, v); return "Rolle geändert."; }} />
                              <ActionButton label="Länder" icon={Globe2} testId={`change-countries-${m.id}`} onDone={st.reload} submitLabel="Speichern"
                                page={`laender-${m.id}`} title="Zuständige Länder ändern" pageRows={[["Teammitglied", m.name]]} pageSubject={m.name}
                                fields={[{ name: "countries", label: "Zuständige Länder (leer = alle)", type: "checks", options: COUNTRY_OPTIONS, default: m.countries }]}
                                onSubmit={async (v) => { await api.post(`/team/${m.id}/countries`, v); return "Länder gespeichert."; }} />
                              {m.role === "org_staff" && <OrgScopeButton m={m} onDone={st.reload} />}
                              <ActionButton label="2FA neu einrichten" icon={KeyRound} testId={`reset-2fa-${m.id}`} onDone={st.reload}
                                submitLabel="2FA neu einrichten" danger
                                description="Der bisherige Authenticator und alle Wiederherstellungscodes der Person werden ungültig, alle ihre Sitzungen enden. Beim nächsten Anmelden mit Passwort erscheint ein neuer QR-Code."
                                fields={[{ type: "info", text: "Nur auslösen, wenn du die Identität der Person persönlich geprüft hast." },
                                  { name: "confirm_email", label: `Zur Bestätigung E-Mail eingeben (${m.email})`, required: true },
                                  { name: "reason", label: "Begründung", type: "textarea", required: true }]}
                                onSubmit={async (v) => { await api.post(`/team/${m.id}/reset-2fa`, v); return "2FA-Neueinrichtung ausgelöst."; }} />
                            </div>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Loadable>
      </Section>
      {isSuper && <RecoveryRequestsCard />}
      {isSuper && <Invitations />}
    </div>
  );
}

const SUFFIX = { view: "ansehen", manage: "bearbeiten", approve: "freigeben", moderate: "moderieren", notes: "Notizen", export: "exportieren", send: "versenden" };

export function Roles() {
  const st = useLoad("/team/roles");
  return (
    <Section title={<span className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-brand-magenta" /> Rollenrechte</span>} testId="settings-roles">
      <Loadable state={st}>
        {(d) => (
          <>
            <p className="mb-4 rounded-lg bg-secondary/70 p-3 text-sm">{d.note}</p>
            <p className="mb-4 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900" data-testid="roles-readonly-note">
              Rollenrechte sind serverseitig festgelegt und hier nur lesbar. Eine Bearbeitung mit Begründung und Rechtevergleich ist bewusst nicht freigeschaltet: Jede Rechteänderung braucht eine gesonderte Freigabe.
            </p>
            <div className="overflow-x-auto">
              <Table data-testid="roles-matrix">
                <TableHeader><TableRow><TableHead>Bereich</TableHead>{d.roles.map((r) => <TableHead key={r.role}>{r.label}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {Object.entries(d.areas).map(([area, text]) => (
                    <TableRow key={area}>
                      <TableCell className="font-medium">{text}</TableCell>
                      {d.roles.map((r) => {
                        const perms = r.all ? ["alle Rechte"] : r.permissions.filter((p) => p.startsWith(`${area}:`)).map((p) => SUFFIX[p.split(":")[1]] || p);
                        return <TableCell key={r.role} className="text-xs">{perms.length ? perms.join(", ") : "–"}</TableCell>;
                      })}
                    </TableRow>
                  ))}
                  <TableRow><TableCell className="font-medium">Audit-Log</TableCell>{d.roles.map((r) => <TableCell key={r.role} className="text-xs">{r.all ? "alle Rechte" : "–"}</TableCell>)}</TableRow>
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </Loadable>
    </Section>
  );
}

const DATA_FLOW = {
  identitaet_bestaetigt: [["identität_prüfen"], "Identität bestätigt"], rueckfrage: [["identität_prüfen", "in_prüfung"], "Rückfrage stellen"],
  fortsetzen: [["rückfrage"], "Bearbeitung fortsetzen"], abschliessen: [["in_prüfung"], "Abschließen"],
  ablehnen: [["identität_prüfen", "in_prüfung", "rückfrage"], "Ablehnen"],
};

const ROLE_TEXT = { verantwortlich: "verantwortlich", "übertragung_angefragt": "Übertragung an diese Person angefragt" };

function Responsibilities({ r }) {
  const dep = r.responsibilities;
  if (!dep) return null;
  return (
    <div className="mt-2 rounded-lg border border-border bg-secondary/40 p-3 text-xs" data-testid={`data-request-deps-${r.id}`}>
      <p className="font-semibold text-brand-purple">Abhängige Kreisverantwortungen · automatisch ermittelt {fmt(dep.checked_at)}</p>
      {dep.groups.length === 0 && dep.hidden_count === 0 && (
        <p className="mt-1 text-zinc-700" data-testid={`data-request-deps-empty-${r.id}`}>Keine offenen Verantwortungen gefunden.</p>
      )}
      {dep.groups.length > 0 && (
        <ul className="mt-1 space-y-1">
          {dep.groups.map((g) => (
            <li key={`${g.group_id}-${g.role}`} className="break-words text-zinc-800" data-testid={`data-request-dep-${r.id}-${g.group_id}`}>
              <Link to={`/kreise-gruppen/${g.group_id}`} className="font-medium text-brand-purple underline">{g.name}</Link>
              {` · ${ROLE_TEXT[g.role]} · ${g.members_count} ${g.members_count === 1 ? "Mitglied" : "Mitglieder"} · ${g.status}`}
              {g.transfer_open && " · Übertragung läuft"}
            </li>
          ))}
        </ul>
      )}
      {dep.hidden_count > 0 && (
        <p className="mt-1 text-zinc-700" data-testid={`data-request-deps-hidden-${r.id}`}>
          {dep.hidden_count} weitere ohne Einsicht (fehlende Kreis- oder Länderrechte).
        </p>
      )}
      <p className="mt-1 text-muted-foreground">Es wird nichts automatisch übertragen oder gelöscht. Übertragung im Kreisdetail über „Verantwortung übertragen“.</p>
    </div>
  );
}

const openCount = (r) => (r.responsibilities ? r.responsibilities.groups.length + r.responsibilities.hidden_count : 0);

const affected = (r) => {
  if (openCount(r) === 0) return "Keine offene Kreisverantwortung";
  const dep = r.responsibilities;
  const names = dep.groups.map((g) => `${g.name} (${ROLE_TEXT[g.role]}${g.transfer_open ? ", Übertragung läuft" : ""})`);
  return [...names, ...(dep.hidden_count ? [`${dep.hidden_count} weitere ohne Einsicht`] : [])].join(" · ");
};

function DataRequests() {
  const st = useLoad("/data-requests");
  return (
    <Section title="Datenanfragen" testId="settings-data-requests">
      <p className="mb-4 text-sm text-muted-foreground">Kontolöschung oder Auskunft: Identität prüfen, Abhängigkeiten klären, abschließen. Eine Löschung lässt sich erst ohne offene Kreisverantwortung abschließen; sie sperrt dann das Konto und markiert es für den dokumentierten Löschprozess.</p>
      <Loadable state={st}>
        {(d) => (d.requests.length === 0 ? <Empty text="Keine Datenanfragen." /> : (
          <ul className="divide-y divide-border">
            {d.requests.map((r) => {
              const del = r.type === "loeschung";
              const blocked = del && openCount(r) > 0; // the server refuses the final step as well
              const options = Object.entries(DATA_FLOW).filter(([value, [from]]) => from.includes(r.status) && !(blocked && value === "abschliessen"))
                .map(([value, [, l]]) => ({ value, label: l }));
              return (
                <li key={r.id} className="flex flex-wrap items-start justify-between gap-2 py-3" data-testid={`data-request-${r.id}`}>
                  <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                    <p className="break-words font-medium text-brand-purple">{r.type_label} · {r.member_name}</p>
                    <p className="text-xs text-muted-foreground">Erfasst {fmt(r.created_at)} · Identität: {r.identity}
                      {!r.responsibilities && ` · Kreisverantwortung bei Erfassung: ${r.dependencies?.group_responsibilities ?? 0}`}</p>
                    <Responsibilities r={r} />
                  </div>
                  <div className="flex items-center gap-2"><Status value={r.status} />
                    {options.length > 0 && <ActionButton label="Bearbeiten" icon={Pencil} testId={`process-data-request-${r.id}`} onDone={st.reload} submitLabel="Schritt speichern"
                      page={`datenanfrage-${r.id}`} pageSubtitle={del ? "Offene Verantwortungen müssen vor Bearbeitung der Löschanfrage geklärt werden."
                        : "Prüfe Identität, Umfang und offene Verantwortungen der Datenanfrage."}
                      pageSubject={`${r.type_label} · ${r.member_name}`}
                      pageRows={[["Anfrage", `${r.type_label} · ${r.member_name}`], ["Erfasst", fmt(r.created_at)], ["Identität", label(r.identity)],
                        ["Status", label(r.status)], ["Offene Kreisverantwortungen", String(openCount(r))],
                        ...(del ? [["Betroffene Kreise", affected(r)],
                          ["Löschung", "Noch keine Löschung ausgelöst · Abschließen erst ohne offene Kreisverantwortung; sperrt dann das Konto und markiert es für den dokumentierten Löschprozess"]] : []),
                        ["Privater Access Pass", "Wird über diese Anfrage nicht angezeigt"]]}
                      pageNotice={{ title: "Vor der Entscheidung", text: "Jeder Schritt braucht eine Notiz und bleibt im Verlauf nachvollziehbar. Es wird nichts automatisch übertragen oder gelöscht.", tone: "lavender" }}
                      title={del ? "Kontolöschanfrage prüfen" : "Datenanfrage bearbeiten"}
                      fields={[
                        ...(blocked ? [{ type: "info", text: `Abschließen ist gesperrt, solange ${openCount(r)} offene Kreisverantwortung(en) bestehen. Übertragung im Kreisdetail über „Verantwortung übertragen“ – die neue Person muss zustimmen. Alle anderen Schritte bleiben möglich.` }] : []),
                        { name: "action", label: "Nächster Schritt", type: "select", options, required: true },
                        { name: "note", label: "Notiz zum Schritt", type: "textarea", required: true }]}
                      onSubmit={async (v) => { await api.post(`/data-requests/${r.id}/transition`, v); return "Schritt gespeichert."; }} />}
                  </div>
                </li>
              );
            })}
          </ul>
        ))}
      </Loadable>
    </Section>
  );
}

const RETENTION_FIELDS = [["Datenart", "category"], ["Zweck", "purpose"], ["Zuständig", "role"], ["Frist", "period"]];
// Desktop: each column is capped and long words break, so one very long term never squeezes the others.
const RETENTION_CELL = { category: "max-w-[20rem]", purpose: "max-w-[24rem]", role: "max-w-[16rem]", period: "max-w-[16rem]" };

function RetentionCards({ entries, testId, label }) {
  // Below md the same rules as labelled cards: every field readable without sideways scrolling.
  return (
    <ul className="space-y-3 md:hidden" aria-label={label} data-testid={`${testId}-cards`}>
      {entries.map((e, i) => (
        <li key={e.category} className="rounded-xl border border-[#E3DDF0] bg-white p-4" data-testid={`${testId}-card-${i}`}>
          <dl className="grid grid-cols-[minmax(0,6rem)_minmax(0,1fr)] gap-x-3 gap-y-2 text-sm">
            {RETENTION_FIELDS.map(([name, key]) => (
              <Fragment key={key}>
                <dt className="font-medium text-muted-foreground">{name}</dt>
                <dd className={`min-w-0 [overflow-wrap:anywhere] ${key === "category" || key === "period" ? "font-semibold text-brand-purple" : "text-zinc-800"}`}
                  data-testid={`${testId}-card-${i}-${key}`}>{e[key]}</dd>
              </Fragment>
            ))}
          </dl>
        </li>
      ))}
    </ul>
  );
}

function RetentionTable({ entries, testId, label }) {
  return (
    <>
      <div className="hidden md:block">
        <Table data-testid={testId}>
          <TableHeader><TableRow><TableHead>Datenart</TableHead><TableHead>Zweck</TableHead><TableHead>Zuständig</TableHead><TableHead>Frist</TableHead></TableRow></TableHeader>
          <TableBody>{entries.map((e, i) => (
            <TableRow key={e.category}>
              {RETENTION_FIELDS.map(([, key]) => (
                <TableCell key={key}><div className={`${RETENTION_CELL[key]} [overflow-wrap:anywhere]`} data-testid={`${testId}-row-${i}-${key}`}>{e[key]}</div></TableCell>
              ))}
            </TableRow>
          ))}</TableBody>
        </Table>
      </div>
      <RetentionCards entries={entries} testId={testId} label={label} />
    </>
  );
}

function RetentionProposal({ d, reload }) {
  const p = d.proposal;
  return (
    <div className="mt-5 space-y-3 rounded-xl border border-[#E3DDF0] bg-[#F7F5FB] p-4" data-testid="retention-proposal">
      <p className="text-sm font-semibold text-brand-purple">Offener Vorschlag · von {p.by || "unbekannt"}{p.at && ` · ${fmt(p.at)}`} · ändert die bestätigten Regeln erst nach Freigabe</p>
      <RetentionTable entries={p.entries} testId="retention-proposal-table" label="Offener Vorschlag" />
      {p.own && <p className="text-sm text-zinc-700" data-testid="retention-own-proposal">Eigener Vorschlag: Freigabe nur durch eine unabhängige, bereits berechtigte Person. Der Vorschlag bleibt bis dahin offen.</p>}
      {!p.own && !d.can_approve && <p className="text-sm text-zinc-700" data-testid="retention-waiting">Wartet auf die Freigabe durch einen Super Admin.</p>}
      {d.can_approve && <RunButton label="Vorschlag freigeben" icon={Check} testId="approve-retention" success="Aufbewahrungsfristen bestätigt."
        onDone={reload} run={() => api.post("/settings/retention/approve", { proposal_id: p.id })} />}
    </div>
  );
}

function Retention() {
  const { user } = useAuth();
  const st = useLoad("/settings/retention");
  const base = st.data?.proposal?.entries || st.data?.entries || [];
  return (
    <Section title="Datenschutz und Aufbewahrung" testId="settings-retention" actions={st.data && can(user, "data_requests:manage") && (
      <ActionButton label="Fristen vorschlagen" icon={Pencil} testId="propose-retention" onDone={st.reload} submitLabel="Zur Prüfung vorlegen"
        page="aufbewahrung" title="Datenschutz und Aufbewahrung" pageSubtitle="Aufbewahrungsregeln benötigen eine verantwortliche und verbindliche Prüfung."
        pageSubject="Aufbewahrungsfristen" pageRows={[["Bestätigte Regeln", label(st.data.status)],
          ["Offener Vorschlag", st.data.proposal ? `von ${st.data.proposal.by || "unbekannt"} · wird durch deinen Vorschlag ersetzt` : "Keiner"],
          ["Freigabe", "Durch eine unabhängige, berechtigte Person (Super Admin) – nie durch die vorschlagende Person"],
          ["Wirkung", "Ein Vorschlag ändert die bestätigten Regeln nicht"], ["Datenkategorien", String(base.length)]]}
        fields={base.map((e, i) => ({ name: `p${i}`, label: `Frist: ${e.category}`, default: e.period, required: true }))}
        onSubmit={async (v) => { await api.put("/settings/retention", { entries: base.map((e, i) => ({ ...e, period: v[`p${i}`] })) }); return "Vorschlag gespeichert."; }} />
    )}>
      <Loadable state={st}>
        {(d) => (
          <>
            <p className="mb-3 rounded-lg bg-orange-50 p-3 text-sm text-orange-800">{d.note}</p>
            <p className="mb-2 text-sm font-semibold text-brand-purple" data-testid="retention-table-title">
              {d.recorded_status === "bestätigt" && !d.defaults ? "Freigegebener Stand" : "Aktueller Stand (nicht freigegeben)"}
            </p>
            <RetentionTable entries={d.entries} testId="retention-table"
              label={d.recorded_status === "bestätigt" && !d.defaults ? "Freigegebener Stand" : "Aktueller Stand (nicht freigegeben)"} />
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm" data-testid="retention-status">Status: <Status value={d.status} />
              <span className="text-zinc-700" data-testid="retention-open-count">
                {d.open_count === 0 ? `Alle ${d.total} Fristen festgelegt` : `${d.open_count} von ${d.total} Fristen noch offen`}
              </span>
              {d.approved_by && <span className="text-muted-foreground" data-testid="retention-approved-by">letzte Freigabe durch {d.approved_by}</span>}
            </div>
            {d.proposal && <RetentionProposal d={d} reload={st.reload} />}
            <p className="mt-3 text-xs text-muted-foreground" data-testid="retention-execution">{d.execution}</p>
          </>
        )}
      </Loadable>
    </Section>
  );
}

function Integrations() {
  const { user } = useAuth();
  const st = useLoad("/settings/integrations");
  return (
    <Section title="Integrationen und Verbindungen" testId="settings-integrations" actions={can(user, "system:view") && (
      <Link to="/systemstatus/aktion/integrationen" data-testid="integrations-full-view-link" className="text-sm font-semibold text-brand-purple underline">Als eigene Ansicht öffnen</Link>
    )}>
      {can(user, "settings:view") && <HandoffDocs />}
      <Loadable state={st}>
        {(d) => (
          <ul className="divide-y divide-border">
            {d.integrations.map((i) => (
              <li key={i.name} className="flex flex-wrap items-center justify-between gap-2 py-3" data-testid={`integration-${i.name}`}>
                <div><p className="font-medium">{i.name}</p><p className="text-xs text-muted-foreground">{i.scope} · {i.note}</p></div>
                <Status value={i.state} />
              </li>
            ))}
          </ul>
        )}
      </Loadable>
    </Section>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tabs = useTabs([["regional", "Sprache & Länder"], ["team", "Team"], ["rollen", "Rollenrechte"],
    can(user, "data_requests:manage") && ["daten", "Datenanfragen"], ["aufbewahrung", "Datenschutz & Aufbewahrung"], ["integrationen", "Integrationen"],
    user?.role === "super_admin" && ["konfiguration", "Konfiguration"]].filter(Boolean).map(([value, l]) => ({ value, label: l })), params.get("tab"));
  // Unknown or not permitted tab values fall back to the first tab ("regional"), so content always matches the active tab.
  const View = { regional: Regional, team: Team, rollen: Roles, daten: DataRequests, aufbewahrung: Retention, integrationen: Integrations,
    konfiguration: AdminConfigStatus }[tabs.value];
  return (
    <div>
      <PageHeader title="Einstellungen" subtitle="Länder, Team, Rollen, Datenanfragen und Verbindungen." icon={SettingsIcon} />
      <Tabbar tabs={tabs} onChange={(v) => setParams({ tab: v })} testId="settings-tabs" />
      <TabPanel tabs={tabs} testId="settings-tabs-panel"><View /></TabPanel>
    </div>
  );
}
